import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Button, 
  CircularProgress, 
  Alert,
  Container,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip
} from '@mui/material';
import { 
  Dataset as DataIcon,
  NavigateNext as NextIcon,
  TableView as TableIcon,
  ArrowBack as BackIcon,
  Visibility as PreviewIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import branding from '../../styles/branding';
import './PreviewPage.css';
import useApi from '../../hooks/useApi';
import { useProjectProgress } from '../../contexts/ProjectProgressContext';
import Breadcrumb from '../../components/common/Breadcrumb';
import DataAnalyzerView from '../../components/Preview/DataAnalyzerView';
import { getApiErrorMessage } from '../../utils/errorMessages';
import { useErrorToast } from '../../components/common';

const PreviewPage = () => {
  // Identifier can be projectId, fileName, or tableName
  const { jobId } = useParams(); // Keep jobId for backward compatibility, but it can be any identifier
  const navigate = useNavigate();
  const api = useApi();
  const { updateCurrentStep } = useProjectProgress();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [viewMode, setViewMode] = useState('preview');
  const [analyzeData, setAnalyzeData] = useState(null);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [analyzeError, setAnalyzeError] = useState(null);
  const { showErrorToast } = useErrorToast();

  useEffect(() => {
    if (error) {
      showErrorToast(error);
      setError(null);
    }
  }, [error, showErrorToast]);

  useEffect(() => {
    if (analyzeError) {
      showErrorToast(analyzeError);
      setAnalyzeError(null);
    }
  }, [analyzeError, showErrorToast]);
  
  // Update current step when component mounts
  useEffect(() => {
    updateCurrentStep('preview');
  }, [updateCurrentStep]);

  useEffect(() => {
    const fetchPreviewData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/preview/${jobId}`);
        
        if (response.data?.success) {
          setPreviewData(response.data.data);
        } else {
          throw new Error('Failed to fetch preview data');
        }
      } catch (err) {
        console.error('Error fetching preview data:', err);
        setError(getApiErrorMessage(err, 'Failed to load preview data'));
      } finally {
        setLoading(false);
      }
    };

    if (jobId) {
      fetchPreviewData();
    } else {
      setError('Job ID is required');
      setLoading(false);
    }
  }, [jobId, api]);

  useEffect(() => {
    if (viewMode !== 'analyzer' || analyzeData || analyzeLoading) return;
    const fetchAnalyzeData = async () => {
      try {
        setAnalyzeLoading(true);
        setAnalyzeError(null);
        const response = await api.get(`/preview/${jobId}?analyze=true`);
        if (response.data?.success) {
          setAnalyzeData(response.data.data);
        } else {
          throw new Error('Failed to fetch analysis data');
        }
      } catch (err) {
        console.error('Error fetching analysis data:', err);
        setAnalyzeError(getApiErrorMessage(err, 'Failed to load analysis'));
      } finally {
        setAnalyzeLoading(false);
      }
    };
    fetchAnalyzeData();
  }, [viewMode, jobId, api, analyzeData, analyzeLoading]);

  const handleViewModeChange = (_, newMode) => {
    if (newMode !== null) setViewMode(newMode);
  };

  const handleContinueToFilters = () => {
    // Store the identifier (projectId/fileName/tableName) in localStorage for the filters page
    localStorage.setItem("currentIdentifier", jobId);
    navigate(`/filters/${jobId}`);
  };

  const formatCellValue = (value) => {
    if (value === null || value === undefined) {
      return <span style={{ color: '#999', fontStyle: 'italic' }}>null</span>;
    }
    if (typeof value === 'string' && value.length > 50) {
      return value.substring(0, 50) + '...';
    }
    return String(value);
  };

  if (loading) {
    return (
      <>
        <Breadcrumb currentStep="preview" />
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress size={60} sx={{ color: branding.colors.primary }} />
            <Typography variant="h6" sx={{ ml: 2, fontFamily: branding.fonts.primary }}>
              Loading preview data...
            </Typography>
          </Box>
        </Container>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Breadcrumb currentStep="preview" />
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Button 
            variant="contained" 
            onClick={() => navigate('/landing')}
            sx={branding.button.sx}
            startIcon={<BackIcon />}
          >
            Back to Upload
          </Button>
        </Container>
      </>
    );
  }

  if (!previewData) {
    return (
      <>
        <Breadcrumb currentStep="preview" />
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Alert severity="warning">
            No preview data available
          </Alert>
        </Container>
      </>
    );
  }

  const { project, columns, previewData: rows, totalColumns, previewRows } = previewData;
  // Backward compatibility: use project if available, otherwise fallback to job
  const projectInfo = project || previewData.job || {};
  // Use projectInfo for all references (renamed from 'job' for clarity)
  const job = projectInfo; // Keep 'job' variable name for existing code compatibility

  return (
    <>
      <Breadcrumb currentStep="preview" />
      <Container maxWidth="xl" sx={{ py: 2, pb: 2 }}>
        {/* Header Section with Data Summary */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap' }}>
          {/* Left Side: Title and Description */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography 
              variant="h5" 
              gutterBottom 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                fontFamily: branding.fonts.primary,
                fontWeight: 600,
                color: branding.colors.primary,
                mb: 0.5,
                fontSize: '20px'
              }}
            >
              {viewMode === 'preview' 
                ? <PreviewIcon sx={{ fontSize: 20, color: branding.colors.primary }} /> 
                : <AnalyticsIcon sx={{ fontSize: 20, color: branding.colors.primary }} />}
              {viewMode === 'preview' ? 'Data Preview' : 'Data Analyzer'}
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#666',
                fontFamily: branding.fonts.primary,
                fontSize: '12px',
                mb: 1
              }}
            >
              Review your uploaded data before proceeding to filters
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontFamily: branding.fonts.primary, 
                  fontSize: '13px', 
                  color: '#888',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word'
                }}
              >
                <strong>Project:</strong> {job.displayName}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontFamily: branding.fonts.primary, 
                  fontSize: '13px', 
                  color: '#888'
                }}
              >
                <strong>Uploaded:</strong> {job.createdDate ? new Date(job.createdDate).toLocaleDateString() : 'N/A'}
              </Typography>
            </Box>
          </Box>
          
          {/* Right Side: Toggle + Data Summary Box */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1.5 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontFamily: branding.fonts.primary,
                  fontWeight: 500,
                  fontSize: '12px',
                  px: 2,
                  py: 0.5,
                  borderColor: '#d0d0d0',
                  color: '#666',
                  '&.Mui-selected': {
                    backgroundColor: branding.colors.primary,
                    color: '#fff',
                    '&:hover': { backgroundColor: '#333' },
                  },
                },
              }}
            >
              <ToggleButton value="preview">
                <Tooltip title="View sample rows">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PreviewIcon sx={{ fontSize: 16 }} />
                    Preview
                  </Box>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="analyzer">
                <Tooltip title="Column profiling &amp; data quality">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AnalyticsIcon sx={{ fontSize: 16 }} />
                    Data Analyzer
                  </Box>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>

          </Box>
        </Box>

        {/* Conditional: Preview Table or Data Analyzer */}
        {viewMode === 'preview' ? (
          <Paper 
            sx={{ 
              mb: 1.5,
              borderRadius: '16px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: '1px solid #e0e0e0',
              overflow: 'hidden',
              flex: 1
            }}
          >
            <TableContainer sx={{ maxHeight: '60vh', overflow: 'auto' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((col, index) => (
                      <TableCell 
                        key={index}
                        sx={{ 
                          fontWeight: 'bold',
                          backgroundColor: '#DCEFF2',
                          color: branding.colors.primary,
                          minWidth: '6.25rem',
                          fontFamily: branding.fonts.primary,
                          borderRight: '1px solid #e0e0e0',
                          py: 1,
                          px: 1.5
                        }}
                      >
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '12px' }}>
                            {col.name}
                          </Typography>
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows && rows.length > 0 ? (
                    rows.slice(0, 10).map((row, rowIndex) => (
                      <TableRow 
                        key={rowIndex} 
                        hover
                        sx={{ 
                          '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                          '&:hover': { backgroundColor: '#f0f0f0' }
                        }}
                      >
                        {columns.map((col, colIndex) => (
                          <TableCell 
                            key={colIndex}
                            sx={{ 
                              fontFamily: branding.fonts.primary,
                              fontSize: '12px',
                              borderRight: '1px solid #e0e0e0',
                              py: 0.5,
                              px: 1.5
                            }}
                          >
                            {formatCellValue(row[col.name])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No data available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : (
          <DataAnalyzerView
            data={analyzeData}
            loading={analyzeLoading}
            error={analyzeError}
          />
        )}

        {/* Action Buttons */}
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            pt: 1,
            pb: 1,
            borderTop: '1px solid #e0e0e0',
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Button 
            variant="outlined" 
            size="medium"
            onClick={() => navigate('/landing')}
            startIcon={<BackIcon />}
            sx={{
              borderRadius: '16px',
              textTransform: 'none',
              fontFamily: branding.fonts.primary,
              fontWeight: 500,
              px: 2.5,
              py: 1,
              fontSize: '12px',
              borderColor: branding.colors.primary,
              color: branding.colors.primary,
              '&:hover': {
                borderColor: branding.colors.primary,
                backgroundColor: 'rgba(0,0,0,0.04)'
              }
            }}
          >
            Back to Upload
          </Button>
          
          <Button 
            variant="contained" 
            size="medium"
            endIcon={<NextIcon />}
            onClick={handleContinueToFilters}
            sx={{
              ...branding.button.sx,
              px: 3,
              py: 1,
              fontSize: '12px',
              fontWeight: 600,
              fontFamily: branding.fonts.primary
            }}
          >
            Continue to Filters
          </Button>
        </Box>
      </Container>
    </>
  );
};

export default PreviewPage;
